import { QueryClient, useQuery } from '@tanstack/react-query';
import type { ArticleDto, BootstrapDto, ItemDto, ShelfDto } from '../shared/api';
import { CONSUMABLE_HOME, FREE_STOCK, TYPES, addr, qcState, type Loc, type StorageType } from '../shared/domain';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, refetchOnWindowFocus: true, retry: 1 } },
});

export async function api<T = unknown>(method: string, url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: { 'x-requested-with': 'boghche', ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = r.headers.get('content-type')?.includes('json') ? await r.json() : null;
  if (!r.ok) {
    if (r.status === 401 && url !== '/api/login') void queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
    throw new ApiError(r.status, data?.error ?? `Error ${r.status}`);
  }
  return data as T;
}
export const get = <T>(url: string) => api<T>('GET', url);
export const post = <T = { ok: true; text?: string }>(url: string, body: unknown = {}) => api<T>('POST', url, body);
export const patch = <T = { ok: true }>(url: string, body: unknown) => api<T>('PATCH', url, body);

// A tab left open on a warehouse PC must not poll all night: that keeps the Neon database
// awake and uses up the free plan. Polling stops after 10 minutes without use.
const IDLE_MS = 10 * 60_000;
let lastUse = Date.now();
for (const ev of ['pointerdown', 'keydown', 'scroll'])
  window.addEventListener(
    ev,
    () => {
      const wasIdle = Date.now() - lastUse > IDLE_MS;
      lastUse = Date.now();
      if (wasIdle) void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    { passive: true },
  );
/** A refetchInterval that pauses while nobody is using the app. */
export const whileInUse = (ms: number) => () => (Date.now() - lastUse > IDLE_MS ? false : ms);

/** Reload all app state after a change. */
export const refresh = () => queryClient.invalidateQueries();

export function useBootstrap() {
  return useQuery({
    queryKey: ['bootstrap'],
    queryFn: () => get<BootstrapDto>('/api/bootstrap'),
    retry: (n, e) => !(e instanceof ApiError && e.status === 401) && n < 1,
  });
}

/** Helpers over the loaded state, like the prototype's lookups. */
export function makeData(b: BootstrapDto) {
  const arts = new Map(b.articles.map((a) => [a.id, a]));
  const shelves = new Map(b.shelves.map((s) => [s.code, s]));
  const projects = new Map(b.projects.map((p) => [p.code, p.name]));
  const items = new Map(b.items.map((i) => [i.no, i]));
  const fallbackArt: ArticleDto = { id: 0, name: '?', type: '2-2', stock: false, unit: 'pc', min: 0, consumable: false, productPart: false, qcNeeded: false, qcEvery: 12, specs: '', supplier: '', price: 0, link: '', linkExpired: false, expiry: null, photo: null };
  const artOf = (i: ItemDto) => arts.get(i.articleId) ?? fallbackArt;
  const shelf = (c: string | null | undefined): ShelfDto | undefined => (c ? shelves.get(c) : undefined);
  const isRepair = (s?: ShelfDto) => s?.areaType === '3';
  const isLocked = (s?: ShelfDto) => s?.areaType === '2-1';
  const pname = (c: string) => projects.get(c) ?? '';
  const projTxt = (c: string) => (c ? `${c} ${pname(c)}`.trim() : '');
  const usedFor = (i: ItemDto) => i.nowFor || i.proj || '';
  const reserved = (i: ItemDto) => !!i.proj && i.proj !== FREE_STOCK;
  const artStock = (a: ArticleDto) => b.items.filter((i) => i.articleId === a.id && i.status === 'in').reduce((s, i) => s + i.qty, 0);
  const artLow = (a: ArticleDto) => a.stock && a.min > 0 && artStock(a) < a.min;
  const homeOfArt = (a: { type: StorageType; consumable: boolean }) => (a.consumable ? CONSUMABLE_HOME : TYPES[a.type].home);
  const qcOf = (i: ItemDto) => {
    const a = artOf(i);
    return qcState(a.qcNeeded, { last: i.qc?.last ?? null, result: i.qc?.result ?? null }, a.qcEvery, b.today);
  };
  const itemsAt = (c: string, lv: number, p: string) => b.items.filter((i) => i.status === 'in' && i.loc?.shelf === c && i.loc.level === lv && i.loc.pos === p);
  const addrLong = (l: Loc | null) => (l ? `${shelf(l.shelf)?.name ?? l.shelf}, level ${l.level}, ${({ L: 'left', M: 'middle', R: 'right' } as const)[l.pos]}` : '');
  const fmtQty = (i: ItemDto) => `${+i.qty.toFixed(2)} ${artOf(i).unit}`;
  const isOwner = b.me.role === 'owner';
  return {
    b,
    me: b.me,
    isOwner,
    today: b.today,
    item: (no: string) => items.get(String(no ?? '').trim().toUpperCase()),
    art: (id: number) => arts.get(id),
    artByName: (n: string) => b.articles.find((a) => a.name.toLowerCase() === n.trim().toLowerCase()),
    artOf,
    shelf,
    isRepair,
    isLocked,
    pname,
    projTxt,
    usedFor,
    reserved,
    artStock,
    artLow,
    homeOfArt,
    homeOf: (i: ItemDto) => homeOfArt(artOf(i)),
    qcOf,
    itemsAt,
    addr,
    addrLong,
    fmtQty,
  };
}
export type Data = ReturnType<typeof makeData>;

/** Resize a photo in the browser before upload (long side 1024 px, JPEG). */
export function readPhoto(file: File, max = 1024): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('Could not read the photo'));
    r.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('This file is not an image'));
      img.onload = () => {
        const s = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.round(img.width * s);
        c.height = Math.round(img.height * s);
        c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL('image/jpeg', 0.8));
      };
      img.src = r.result as string;
    };
    r.readAsDataURL(file);
  });
}
